import { createFileRoute } from "@tanstack/react-router";
import { DM_Masonry_BrickKing } from "common/materials/library/designMaterialPath";
import { assertIsNotNull } from "common/utils/assertOfType";
import { polyLine2New } from "geom/polyLine2Types";
import { getActiveSharedWebGL } from "gfx/activeSharedWebGL";
import { polyLine2ToTHREEStroke, scaleBufferGeometryUVs, xyPlane } from "gfx/convert";
import { LineStrokeMaterialFactory } from "gfx/materials/lineStrokeMaterial";
import { pixelCheckerboardFragmentShader } from "gfx/materials/shaders/pixelCheckerboardFragmentShader";
import { UVVertexShader } from "gfx/threejs/higharc/UVVertexShader";
import type THREEViewerLite from "gfx/THREEViewerLite";
import type { THREEViewerSystem } from "gfx/THREEViewerSystem";
import { SharedWebGLCanvas } from "lib/components/gfx/SharedWebGLCanvas";
import useTHREEViewerLite from "lib/components/studio/editorViewer/useTHREEViewerLite";
import useTHREEViewerSystem from "lib/components/studio/useTHREEViewerSystem";
import getPageTitle from "lib/utils/getPageTitle";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import * as THREE from "three";
import withAuthentication from "../../components/withAuthentication";

const cubeGeometry = new THREE.BoxGeometry(500, 500, 500);
scaleBufferGeometryUVs(cubeGeometry, 20);

const checkerboardMaterial = new THREE.ShaderMaterial({
  vertexShader: UVVertexShader,
  fragmentShader: pixelCheckerboardFragmentShader,
});

function SharedWebGLContextViewers() {
  const [canvasRefreshCount, setCanvasRefreshCount] = useState(0);

  const viewerContainerRefA = useRef<HTMLDivElement>(null);
  const viewerRefA = useRef<THREEViewerLite>(null);
  const animate = false; // The viewers should react to CSS transform changes even if the viewer is not animating.
  useTHREEViewerLite({
    viewerRef: viewerRefA,
    options: {
      animate,
    },
    viewerContainerRef: viewerContainerRefA,
    viewName: "a",
  });

  const viewerContainerRefB = useRef<HTMLDivElement>(null);
  const viewerSystemB = useRef<THREEViewerSystem>(null);
  const { renderID: renderIDB } = useTHREEViewerSystem({
    viewerSystem: viewerSystemB,
    viewerContainerRef: viewerContainerRefB,
    setLoading: () => {},
    animate,
  });

  const [viewerBIsPictureInPicture, setViewerBIsPictureInPicture] = useState(false);

  const swapPictureInPictureViewer = () => {
    if (viewerSystemB.current && viewerRefA.current) {
      const viewerA = viewerRefA.current;
      const viewerB = viewerSystemB.current.viewer;
      if (viewerB.renderOrder === 1) {
        viewerB.renderOrder = 0;
        viewerA.renderOrder = 1;
        setViewerBIsPictureInPicture(false);
      } else {
        viewerB.renderOrder = 1;
        viewerA.renderOrder = 0;
        setViewerBIsPictureInPicture(true);
      }
    }
  };

  useEffect(() => {
    if (viewerRefA.current && viewerSystemB.current) {
      const viewerA = viewerRefA.current;
      viewerA.setSceneBackground(new THREE.Color("#4dd689"));
      viewerA.scene.clear();
      viewerA.scene.add(
        polyLine2ToTHREEStroke({
          path: polyLine2New([-100, -100, 100, -100, 100, 100, -100, 100, -100, -100]),
          plane: xyPlane,
          material: LineStrokeMaterialFactory.instance.getMaterial({}),
        }),
      );
      viewerA.renderOrder = viewerBIsPictureInPicture ? 0 : 1;

      // If the checkerboard is sharp on screen, then everything is fine.
      // If the checkerboard is blurry, then the render target dimensions might be subtly wrong.
      const checkerboardMesh = new THREE.Mesh(new THREE.PlaneGeometry(100, 100), checkerboardMaterial);
      viewerA.scene.add(checkerboardMesh);

      const viewerB = viewerSystemB.current.viewer;
      viewerB.renderOrder = viewerBIsPictureInPicture ? 1 : 0;
      viewerB.setSceneBackground(new THREE.Color("#7799aa"));
      viewerB.skyboxVisible = false;

      void (async () => {
        assertIsNotNull(viewerSystemB.current);
        const brickMaterialB = await viewerSystemB.current.materialFactory.getDesignMaterial(
          DM_Masonry_BrickKing.L.Brick_Acme.Brick_Acme_AdobeWells,
        );
        viewerB.scene.clear();
        const cubeMeshB = new THREE.Mesh(cubeGeometry, brickMaterialB);
        viewerB.scene.add(cubeMeshB);
      })();

      return () => {
        if (viewerSystemB.current) {
          viewerSystemB.current.viewer.scene.clear();
        }
      };
    }
    return () => {};
  }, [renderIDB]);

  useEffect(() => {
    let callback: number;
    function frame() {
      const sharedWebGL = getActiveSharedWebGL();
      if (sharedWebGL) {
        setCanvasRefreshCount(sharedWebGL.getCanvasRefreshCount());
      }
      callback = requestAnimationFrame(frame);
    }
    frame();
    return () => {
      cancelAnimationFrame(callback);
    };
  }, []);

  const commonStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    position: "absolute",
    top: 0,
    left: 0,
    transition: "transform 0.5s ease-in-out",
  };
  const mainViewerStyle: React.CSSProperties = {
    ...commonStyle,
    zIndex: 1,
    transform: "scale(1.0)",
  };
  const pictureInPictureViewerStyle: React.CSSProperties = {
    ...commonStyle,
    zIndex: 10,
    transform: "scale(0.2)",
  };

  return (
    <div style={{ height: "100%" }}>
      <div
        ref={viewerContainerRefA}
        id="viewerA"
        style={viewerBIsPictureInPicture ? mainViewerStyle : pictureInPictureViewerStyle}
      ></div>
      <div
        ref={viewerContainerRefB}
        id="viewerB"
        style={viewerBIsPictureInPicture ? pictureInPictureViewerStyle : mainViewerStyle}
      ></div>
      <div
        style={{
          position: "absolute",
          bottom: 10,
          left: "calc(50% - 250px)",
          width: 500,
          zIndex: 100,
        }}
      >
        <div style={{}}>
          Canvas refresh count: {canvasRefreshCount} (should stay constant while nothing is animating)
        </div>
        <button onClick={swapPictureInPictureViewer} style={{ width: 500 }}>
          Swap picture in picture viewer (you should see an animated transition)
        </button>
      </div>
    </div>
  );
}

function WebGLContextSharingTransformPage() {
  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden" }}>
      <Head>
        <title>{getPageTitle("WebGL Context Sharing Transform Test")}</title>
        <meta name="robots" content="noindex" />
      </Head>
      <SharedWebGLCanvas>
        <SharedWebGLContextViewers></SharedWebGLContextViewers>
      </SharedWebGLCanvas>
    </div>
  );
}

export const Route = createFileRoute("/debug/webgl-context-sharing-transform")({
  component: withAuthentication(WebGLContextSharingTransformPage, { superAdmin: true }),
});
